import { useRef } from 'react';
import type { Query, UseQueryOptions } from '@tanstack/react-query';
import { graphql } from '~/graphql';
import type {
  GetOrderByIdQuery,
  GetOrderMsrSyncStatusQuery,
  OrderFiltersInput,
  OrderStatus,
  TradePerspective,
} from '~/graphql/graphql';
import { useGraphqlMutation } from '~/hooks/useGraphqlMutation';
import { CART_QUERY_KEYS } from '~/utils/cartCache';
import { STALE_TIME_MS, useGraphqlQuery } from '~/hooks/useGraphqlQuery';
import type {
  PaginatedFetchOptions,
  RouteSort,
} from '~/hooks/usePaginatedQuery';
import { BUY_LIST_DETAIL_QUERY_KEY } from '~/api/buyList';

const ORDER_BLOCK_POLL_INTERVAL_MS = 5000;

export const useReceiveOrder = () => {
  const receiveOrderMutation = graphql(`
    mutation receiveOrder($orderId: ID!) {
      receiveOrder(id: $orderId) {
        id
      }
    }
  `);

  return useGraphqlMutation(receiveOrderMutation, ['orders']);
};

export const useConfirmShipmentReceipt = () => {
  const confirmShipmentReceiptMutation = graphql(`
    mutation confirmShipmentReceipt($subOrderId: ID!) {
      confirmShipmentReceipt(subOrderId: $subOrderId) {
        id
      }
    }
  `);

  return useGraphqlMutation(confirmShipmentReceiptMutation, ['orders']);
};

type OrderCountQueryOptions = {
  enabled?: boolean;
  refetchOnWindowFocus?: boolean | 'always';
};

export const useGetBuyerOrdersCountByStatus = (
  options?: OrderCountQueryOptions
) => {
  const buyerOrderCountsQuery = graphql(`
    query getBuyerOrderCounts {
      buyerOrdersCount {
        confirmed
        delivered
        draft
        pendingSellerConfirmation
        pendingSellerConfirmationWithReminders
        shipped
        cancelled
        blocked
      }
    }
  `);

  return useGraphqlQuery(
    buyerOrderCountsQuery,
    ['orderCounts', 'buyer'],
    undefined,
    options
  );
};

export const useGetSellerOrdersCountByStatus = (
  options?: OrderCountQueryOptions
) => {
  const sellerOrderCountsQuery = graphql(`
    query getSellerOrderCounts {
      sellerOrdersCount {
        confirmed
        delivered
        draft
        pendingSellerConfirmation
        pendingSellerConfirmationWithReminders
        shipped
        cancelled
        blocked
      }
    }
  `);

  return useGraphqlQuery(
    sellerOrderCountsQuery,
    ['orderCounts', 'seller'],
    undefined,
    options
  );
};

export const useShipOrder = () => {
  const shipOrderMutation = graphql(`
    mutation ShipOrder(
      $orderId: ID!
      $shippedDate: Instant!
      $deliveryStartDate: Instant!
      $deliveryEndDate: Instant
    ) {
      shipOrder(
        id: $orderId
        shippedDate: $shippedDate
        deliveryStartDate: $deliveryStartDate
        deliveryEndDate: $deliveryEndDate
      ) {
        id
      }
    }
  `);

  return useGraphqlMutation(shipOrderMutation, ['orders', 'packingList']);
};

export const useGetOrderById = (
  orderId: string,
  options?: Partial<UseQueryOptions<GetOrderByIdQuery>>
) => {
  const pollingSince = useRef<number | null>(null);
  // Reset on orderId change: this hook isn't remounted by React Router on a param-only
  // navigation, so a stale pollingSince from the previously viewed order would otherwise
  // survive and skew the 30s cap for the newly viewed one.
  const lastOrderIdRef = useRef<string | null>(null);
  if (lastOrderIdRef.current !== orderId) {
    lastOrderIdRef.current = orderId;
    pollingSince.current = null;
  }
  const query = graphql(`
    query GetOrderById($orderId: ID!) {
      order(id: $orderId) {
        id
        viewerPerspective
        createdAt
        updatedAt
        status
        isBrokered
        parentOrder {
          id
        }
        cancellationNote
        confirmedBy {
          id
          firstName
          lastName
          externalId
        }
        shippedBy {
          id
          firstName
          lastName
          externalId
        }
        blockMessages {
          id
          message
          createdAt
          processedAt
        }
        purchaseOrderNumber
        shippedDate
        deliveryStartDate
        deliveryEndDate
        receivedDate
        buyer {
          firstName
          lastName
        }
        buyerContact {
          id
          firstName
          lastName
          phoneNumber
          externalId
        }
        sellerContacts {
          id
          firstName
          lastName
          phoneNumber
          externalId
        }
        primaryContact {
          id
        }
        shippingLocation {
          id
          name
          facility {
            address {
              addressLine1
              addressLine2
              city
              region
              zipCode
              latitude
              longitude
            }
          }
          organization {
            name
          }
        }
        deliveryAddress {
          addressLine1
          addressLine2
          city
          region
          zipCode
          country
          latitude
          longitude
        }
        buyingOrganization {
          name
          msrSyncEnabled
        }
        canManuallySyncWithMsr
        canViewMsrPurchaseOrder
        msrPurchaseOrderEmails {
          id
          recipientTo
          requestedBy {
            id
            firstName
            lastName
          }
          createdAt
          status
        }
        canReturnToDraft
        msrSyncedAt
        msrPurchaseOrderInputs {
          orderedByCode
          buyerCode
          cashContract
          numDaysIntransit
          unitType
          isImport
          readyAtMill
          incoTerms
          incoTermsLoc
        }
        canChoosePrimaryContact
        awaitingPurchaseOrderNumber
        sellingOrganization {
          id
          name
          sellingOrganizationCode
        }
        sellingOrganizationFacility {
          address {
            addressLine1
            addressLine2
            city
            region
            country
          }
        }
        incoTerm
        isInternational
        tarp
        currency
        deliveryMethod
        incotermLoading
        incotermDischarge
        incotermAddressDestination
        portOfLoading {
          id
          code
          name
          city
          country
          type
        }
        portOfDischarge {
          id
          code
          name
          city
          country
          type
        }
        shippingRoute {
          id
          forwarder
          transitDays
        }
        shippingRouteSnapshot {
          id
          forwarder
          transitDays
          freightCost {
            amount
            currency
          }
          terminalHandlingCharge {
            amount
            currency
          }
          gassingCost {
            amount
            currency
          }
          additionalCostsPerContainer {
            amount
            currency
          }
          additionalCostsPerShipment {
            amount
            currency
          }
          createdAt
        }
        shippingFee {
          amount
          currency
        }
        transportVehicle {
          id
          name
          vehicleType
        }
        documents {
          id
          documentType
          uploadType
          originalFilename
          fileSizeBytes
          mimeType
          createdAt
        }
        items {
          effectivePiecesPerBundle
          piecesPerBundleOverride
          quantity(mode: { mode: USER_PREFERRED }) {
            value
            unit
          }
          piecesQuantity: quantity(mode: { mode: STORED }) {
            value
          }
          quantityStored: quantity(mode: { mode: STORED }) {
            value
          }
          price {
            amount
            currency
          }
          suggestedPrice {
            amount
            currency
          }
          effectivePrice {
            amount
            currency
          }
          pickupPrice {
            amount
            currency
          }
          deliveryPrice {
            amount
            currency
          }
          pricePerPiece {
            amount
            currency
          }
          suggestedPricePerPiece {
            amount
            currency
          }
          pickupPricePerPiece {
            amount
            currency
          }
          deliveryPricePerPiece {
            amount
            currency
          }
          platformProductsLengths {
            id
            isNominal
            lengthDisplayValue
            platformProduct {
              id
              name
              category
              actualWidthInches
              actualThicknessInches
              nominalWidthInches
              nominalThicknessInches
              actualLengthInches
              standardUnitsCode
              properties
              piecesPerBundle {
                value
                unit
              }
            }
            platformProductVehicleCapacities {
              transportVehicle {
                id
              }
              capacity {
                value
                unit
              }
            }
            length {
              id
              actualLengthInches
              nominalLengthInches
            }
          }
          tallyPlatformProduct {
            id
          }
          cannedTally {
            id
          }
          buyCode
        }
        subOrders {
          id
          status
          sellingOrganization {
            name
            sellingOrganizationCode
          }
          items {
            quantity(mode: { mode: USER_PREFERRED }) {
              value
              unit
            }
            platformProductsLengths {
              id
              lengthDisplayValue
              platformProduct {
                name
              }
            }
          }
        }
        shipments {
          id
          status
          items {
            quantity(mode: { mode: USER_PREFERRED }) {
              value
              unit
            }
            platformProductsLengths {
              id
              lengthDisplayValue
              platformProduct {
                name
              }
            }
          }
          packingListItems {
            id
            price {
              amount
              currency
            }
            quantity(mode: { mode: STANDARD }) {
              value
              unit
            }
            platformProductsLengths {
              id
              lengthDisplayValue
              platformProduct {
                name
              }
            }
          }
          shippedDate
          deliveryStartDate
          deliveryEndDate
          receivedDate
        }
      }
    }
  `);

  return useGraphqlQuery(
    query,
    ['orders', orderId],
    { orderId },
    {
      ...options,
      // MSR sync can move this out of PENDING_SELLER_CONFIRMATION with no mutation to invalidate
      // the cache; cap that specific poll at the same 30s the rest of the app treats data as
      // fresh for, then fall through to the caller's own interval (e.g. MSR sync polling in
      // OrderView), which is not subject to this cap.
      refetchInterval: (query: Query<GetOrderByIdQuery>) => {
        if (query.state.data?.order?.status === 'PENDING_SELLER_CONFIRMATION') {
          pollingSince.current ??= Date.now();
          if (Date.now() - pollingSince.current < STALE_TIME_MS) {
            return ORDER_BLOCK_POLL_INTERVAL_MS;
          }
        } else {
          pollingSince.current = null;
        }

        const callerRefetchInterval = options?.refetchInterval;
        return typeof callerRefetchInterval === 'function'
          ? (
              callerRefetchInterval as (
                query: Query<GetOrderByIdQuery>
              ) => number | false
            )(query)
          : (callerRefetchInterval ?? false);
      },
    }
  );
};

// Keyed apart from ['orders', orderId] so invalidateQueries(['orders']) from unrelated mutations
// (ship, confirm, cancel, ...) never forces a refetch here — useMsrSyncToast should only see
// updatedAt change on this query's own poll tick, not on every order write.
export const useGetOrderMsrSyncStatus = (
  orderId: string,
  options?: Partial<UseQueryOptions<GetOrderMsrSyncStatusQuery>>
) => {
  const query = graphql(`
    query GetOrderMsrSyncStatus($orderId: ID!) {
      order(id: $orderId) {
        id
        updatedAt
        purchaseOrderNumber
      }
    }
  `);

  return useGraphqlQuery(
    query,
    ['msrSyncStatus', orderId],
    { orderId },
    options
  );
};

export type MsrPurchaseOrderEmail = NonNullable<
  GetOrderByIdQuery['order']
>['msrPurchaseOrderEmails'][number];

/** The full order shape returned by `useGetOrderById`, reused by the order-builder's sourcing mode. */
export type OrderForBuilder = NonNullable<
  NonNullable<ReturnType<typeof useGetOrderById>['data']>['order']
>;

/** A single masked shipment, as seen by the end buyer on the order-view page. */
export type OrderShipmentForBuyer = OrderForBuilder['shipments'][number];

const ordersQuery = graphql(`
  query getOrders(
    $status: OrderStatus
    $perspective: TradePerspective!
    $filters: OrderFiltersInput
    $page: Int!
    $size: Int!
  ) {
    orders(
      status: $status
      perspective: $perspective
      filters: $filters
      pageInput: { page: $page, size: $size }
    ) {
      paging {
        totalElements
        totalPages
        number
        size
      }
      content {
        createdAt
        id
        purchaseOrderNumber
        deliveryStartDate
        deliveryEndDate
        shippedDate
        parentOrder {
          id
        }
        isBrokered
        buyer {
          firstName
          lastName
          externalId
        }
        buyingOrganization {
          name
          buyingOrganizationCode
        }
        sellingOrganization {
          name
          sellingOrganizationCode
        }
        currency
        shippingLocation {
          name
          code
        }
        deliveryAddress {
          addressLine1
          city
          region
        }
        items {
          quantity(mode: { mode: USER_PREFERRED }) {
            value
            unit
          }
          price {
            amount
            currency
          }
          suggestedPrice {
            amount
            currency
          }
          effectivePrice {
            amount
            currency
          }
          quantityStored: quantity(mode: { mode: STORED }) {
            value
          }
          pricePerPiece {
            amount
            currency
          }
          suggestedPricePerPiece {
            amount
            currency
          }
          platformProductsLengths {
            isNominal
            platformProduct {
              name
              code
              category
              standardUnitsCode
              actualWidthInches
              actualThicknessInches
              nominalWidthInches
              nominalThicknessInches
              actualLengthInches
              piecesPerBundle {
                value
              }
            }
            length {
              actualLengthInches
              nominalLengthInches
            }
          }
        }
        status
      }
    }
  }
`);

// The dashboard widgets that consume this take a fixed unpaginated-feeling slice (sorted and
// sliced client-side down to a handful of rows) rather than driving a scrollable list, so they
// get the platform's max page size instead of a real page.
const DASHBOARD_WIDGET_PAGE_SIZE = 100;

export const useGetOrders = (
  perspective: TradePerspective,
  status?: OrderStatus | undefined,
  filters?: OrderFiltersInput,
  options?: { enabled?: boolean }
) => {
  const hasFilters = Boolean(filters && Object.keys(filters).length > 0);
  const activeFilters = hasFilters ? filters : undefined;

  return useGraphqlQuery(
    ordersQuery,
    [
      'orders',
      perspective,
      status ?? '',
      JSON.stringify(activeFilters ?? null),
    ],
    {
      status,
      perspective,
      filters: activeFilters,
      page: 0,
      size: DASHBOARD_WIDGET_PAGE_SIZE,
    },
    options
  );
};

export type PerspectiveOrder = NonNullable<
  NonNullable<ReturnType<typeof useGetOrders>['data']>['orders']
>['content'][number];

export type OrdersPageParams = {
  perspective: TradePerspective;
  status?: OrderStatus;
  filters?: OrderFiltersInput;
};

/** `usePaginatedQuery`-shaped fetch hook, for a real scrollable orders list. */
export const useGetOrdersPage = (
  page: number,
  size: number,
  params?: OrdersPageParams,
  _sort?: RouteSort,
  options?: PaginatedFetchOptions
) => {
  const filters = params?.filters;
  const hasFilters = Boolean(filters && Object.keys(filters).length > 0);
  const activeFilters = hasFilters ? filters : undefined;

  return useGraphqlQuery(
    ordersQuery,
    [
      'orders',
      params?.perspective ?? '',
      params?.status ?? '',
      JSON.stringify(activeFilters ?? null),
      page,
      size,
    ],
    {
      status: params?.status,
      perspective: params?.perspective as TradePerspective,
      filters: activeFilters,
      page,
      size,
    },
    options
  );
};

export const useGetOrdersByPerspective = (
  perspective: TradePerspective,
  status?: OrderStatus | undefined,
  filters?: OrderFiltersInput
) => {
  const { data, isLoading, isError } = useGetOrders(
    perspective,
    status,
    filters
  );

  return {
    orders: data?.orders?.content ?? [],
    isLoading,
    isError,
  };
};

export const useGetOrdersCountByPerspective = (
  perspective: TradePerspective
) => {
  const buyer = useGetBuyerOrdersCountByStatus({
    enabled: perspective === 'BUYING',
  });
  const seller = useGetSellerOrdersCountByStatus({
    enabled: perspective === 'SELLING',
  });

  const counts =
    perspective === 'BUYING'
      ? buyer.data?.buyerOrdersCount
      : seller.data?.sellerOrdersCount;

  return { counts };
};

export const useConfirmOrder = () => {
  const confirmOrderMutation = graphql(`
    mutation confirmOrder(
      $orderId: ID!
      $shippedDate: Instant
      $deliveryStartDate: Instant
      $deliveryEndDate: Instant
    ) {
      confirmOrder(
        id: $orderId
        shippedDate: $shippedDate
        deliveryStartDate: $deliveryStartDate
        deliveryEndDate: $deliveryEndDate
      ) {
        id
      }
    }
  `);

  return useGraphqlMutation(confirmOrderMutation, ['orders', 'orderCounts']);
};

export const useCalculateShipDates = (orderId: string) => {
  const query = graphql(`
    query CalculateShipDates($orderId: ID!) {
      calculateShipDates(orderId: $orderId) {
        shippedDate
        deliveryStartDate
        deliveryEndDate
      }
    }
  `);

  return useGraphqlQuery(
    query,
    ['calculateShipDates', orderId],
    {
      orderId,
    },
    { enabled: false }
  );
};

export const usePlaceOrder = () => {
  const placeOrderMutation = graphql(`
    mutation placeOrder(
      $orderId: ID!
      $locationId: ID!
      $incoTerm: LogisticsIncoterm!
      $transportVehicleId: ID!
      $tarp: Boolean
      $recipientUserIds: [ID!]
      $primaryContactUserId: ID
      $promiseDate: Date
      $shipWeekDate: Date
      $dueByDate: Date
      $msrPurchaseOrderInput: MsrPurchaseOrderInput
    ) {
      placeOrder(
        orderId: $orderId
        locationId: $locationId
        incoTerm: $incoTerm
        transportVehicleId: $transportVehicleId
        tarp: $tarp
        recipientUserIds: $recipientUserIds
        primaryContactUserId: $primaryContactUserId
        promiseDate: $promiseDate
        shipWeekDate: $shipWeekDate
        dueByDate: $dueByDate
        msrPurchaseOrderInput: $msrPurchaseOrderInput
      ) {
        id
      }
    }
  `);

  // No 'cart' (by-id): checkout is still mounted on that query a moment before navigating
  // away, and the placed cart is no longer a DRAFT, so invalidating it would refetch a 404.
  return useGraphqlMutation(placeOrderMutation, [
    'orders',
    'ordersWithChats',
    'carts',
    BUY_LIST_DETAIL_QUERY_KEY,
  ]);
};

export const useCancelOrder = () => {
  const cancelOrderMutation = graphql(`
    mutation CancelOrder($id: ID!, $note: String!) {
      cancelOrder(id: $id, note: $note) {
        id
        status
      }
    }
  `);

  return useGraphqlMutation(cancelOrderMutation, ['orders', 'orderCounts']);
};

export const useCopyOrderToCart = () => {
  const copyOrderToCartMutation = graphql(`
    mutation copyOrderToCart($orderId: ID!) {
      copyOrderToCart(orderId: $orderId) {
        cart {
          id
        }
        skippedItems {
          productName
          reason
        }
      }
    }
  `);

  return useGraphqlMutation(copyOrderToCartMutation, [
    ...CART_QUERY_KEYS,
    BUY_LIST_DETAIL_QUERY_KEY,
  ]);
};

export const useAddRequestForQuoteToCart = () => {
  const addRequestForQuoteToCartMutation = graphql(`
    mutation addRequestForQuoteToCart(
      $requestForQuoteId: ID!
      $facilityId: ID!
      $recipientUserIds: [ID!]
    ) {
      addRequestForQuoteToCart(
        requestForQuoteId: $requestForQuoteId
        facilityId: $facilityId
        recipientUserIds: $recipientUserIds
      ) {
        id
      }
    }
  `);

  return useGraphqlMutation(addRequestForQuoteToCartMutation, [
    ...CART_QUERY_KEYS,
    'requestForQuote',
    'draftRequestForQuote',
    'requestForQuotes',
    BUY_LIST_DETAIL_QUERY_KEY,
  ]);
};

export const useRevertBlockedOrderToDraft = () => {
  const revertBlockedOrderToDraftMutation = graphql(`
    mutation RevertBlockedOrderToDraft($id: ID!) {
      revertBlockedOrderToDraft(id: $id) {
        id
        status
      }
    }
  `);

  return useGraphqlMutation(revertBlockedOrderToDraftMutation, [
    'orders',
    'ordersWithChats',
    'carts',
    'orderCounts',
    BUY_LIST_DETAIL_QUERY_KEY,
  ]);
};

export const useResubmitBlockedOrder = () => {
  const resubmitBlockedOrderMutation = graphql(`
    mutation ResubmitBlockedOrder($id: ID!) {
      resubmitBlockedOrder(id: $id) {
        id
        status
      }
    }
  `);

  return useGraphqlMutation(resubmitBlockedOrderMutation, [
    'orders',
    'orderCounts',
  ]);
};

// No invalidateQueries: this only enqueues an async sync job and never changes order data
// synchronously — landing is detected by useMsrRefreshPolling/useGetOrderMsrSyncStatus polling,
// not by refetching here.
export const useSyncOrderWithExternalVendor = () => {
  const mutation = graphql(`
    mutation SyncOrderWithExternalVendor($id: ID!) {
      syncOrderWithExternalVendor(id: $id) {
        id
      }
    }
  `);

  return useGraphqlMutation(mutation);
};

export const useResendOrderPlacedEmail = () => {
  const mutation = graphql(`
    mutation ResendOrderPlacedEmail($orderId: ID!) {
      resendOrderPlacedEmail(orderId: $orderId)
    }
  `);

  return useGraphqlMutation(mutation, ['orders', 'ordersWithChats', 'chats']);
};

export const useEmailMsrPurchaseOrder = () => {
  const mutation = graphql(`
    mutation EmailMsrPurchaseOrder(
      $orderId: ID!
      $contactIds: [ID!]!
      $printOptions: MsrPurchaseOrderPrintOptionsInput
    ) {
      emailMsrPurchaseOrder(
        orderId: $orderId
        contactIds: $contactIds
        printOptions: $printOptions
      )
    }
  `);

  return useGraphqlMutation(mutation, ['orders', 'ordersWithChats', 'chats']);
};

export const useDeclinePriceNegotiation = (orderId: string) => {
  const mutation = graphql(`
    mutation DeclinePriceNegotiation($orderId: ID!, $reason: String) {
      declinePriceNegotiation(orderId: $orderId, reason: $reason) {
        id
      }
    }
  `);

  return useGraphqlMutation(mutation, ['orders', orderId]);
};

export const useShippingSoon = () => {
  const query = graphql(`
    query ShippingSoon {
      shippingSoon {
        id
        purchaseOrderNumber
        status
        deliveryStartDate
        sellingOrganization {
          name
          sellingOrganizationCode
        }
        currency
        items {
          quantity(mode: { mode: USER_PREFERRED }) {
            value
            unit
          }
          price {
            amount
            currency
          }
          suggestedPrice {
            amount
            currency
          }
          effectivePrice {
            amount
            currency
          }
          quantityStored: quantity(mode: { mode: STORED }) {
            value
          }
          pricePerPiece {
            amount
            currency
          }
          suggestedPricePerPiece {
            amount
            currency
          }
          platformProductsLengths {
            isNominal
            platformProduct {
              name
              actualWidthInches
              actualThicknessInches
              nominalWidthInches
              nominalThicknessInches
              actualLengthInches
              piecesPerBundle {
                value
              }
            }
            length {
              actualLengthInches
              nominalLengthInches
            }
          }
        }
      }
    }
  `);

  return useGraphqlQuery(query, ['shippingSoon']);
};
